"use client";

import { useState } from "react";
import Image from "next/image";
import marbleImg from "@/assets/images/marble.webp";
import { RecursiveTranslations } from "@/data/types";
import { fadedIconButtonStyle, h1Style, h5Style } from "@/data/styles";
import Link from "next/link";
import { PhoneIcon, WhatsAppIcon } from "@/components/icons";
import { sendContactForm } from "@/utils/api";
import Toast from "../toast/Toast";

interface Props {
  translations: RecursiveTranslations;
}

export default function ContactFormSegment3({ translations }: Props) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [messageStatus, setMessageStatus] = useState<"success" | "error" | undefined>(undefined);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(undefined);
    setMessageStatus(undefined);

    try {
      const res = await sendContactForm(formData);
      setMessage(res.message || "Your message has been sent successfully!");
      setMessageStatus("success");
      setFormData({ name: "", email: "", phone: "" }); // reset form

      setTimeout(() => {
        setMessage(undefined);
        setMessageStatus(undefined);
      }, 4000);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setMessage("Failed to send your message. Please try again.");
      setMessageStatus("error");

      setTimeout(() => {
        setMessage(undefined);
        setMessageStatus(undefined);
      }, 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="contact-form" className="relative w-full bg-[#c9baa6]">
      <div className="absolute inset-0 z-10">
        <Image src={marbleImg} alt="Marble Background" fill className="object-cover" loading="lazy" />
      </div>

      <div className="relative flex flex-col items-center justify-center px-[20px] md:px-[50px] py-20 max-w-[900px] mx-auto gap-10 z-20">
        <div className="flex flex-col items-center justify-center gap-[5px] text-center">
          <h2 className={h1Style}>{translations?.title as string}</h2>
          <p className={h5Style}>{translations?.subtitle as string}</p>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full max-w-lg">
          {["name", "email", "phone"].map((field, i) => (
            <input
              key={i}
              name={field}
              type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
              placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
              value={formData[field as keyof typeof formData]}
              onChange={handleChange}
              required
              className="w-full rounded-2xl bg-white/45 backdrop-blur-xl border border-white/70 px-5 py-4 text-gray-900 placeholder-gray-700/70 shadow-md focus:shadow-md focus:shadow-gray-900/20 focus:ring-0 outline-none transition-all duration-500"
            />
          ))}

          <button
            aria-label="form-submit-btn"
            type="submit"
            disabled={loading}
            className="mt-4 w-full rounded-full bg-[#161e2d]/90 backdrop-blur-md border border-white/10 
              px-8 py-3 text-white tracking-wide shadow-[0_0_10px_rgba(22,30,45,0.6)] 
              hover:shadow-[0_0_15px_rgba(22,30,45,0.8)] hover:bg-[#161e2d] cursor-pointer
              active:scale-[0.97] transition-all duration-500 disabled:opacity-50"
          >
            {loading ? "Sending..." : translations?.buttonLabel as string}
          </button>

          <p className="text-gray-600 text-sm md:text-base text-center leading-relaxed">
            {translations?.privacyPolicyAgreement as string}
          </p>
        </form>

        {/* CONTACT ICONS */}
        <div className="flex flex-row gap-4 justify-center items-center">
          <Link href="tel:+971585014312" target="_blank" rel="noopener noreferrer" className={fadedIconButtonStyle} aria-label="phone-link">
            <PhoneIcon color="#161e2d" />
          </Link>

          <Link href="https://wa.me/971585014312" target="_blank" rel="noopener noreferrer" className={fadedIconButtonStyle} aria-label="whatsUp-link">
            <WhatsAppIcon color="#161e2d" />
          </Link>
        </div>

        <Toast 
          status={messageStatus} message={message} 
          onClose={() => { setMessage(undefined); setMessageStatus(undefined); }} 
        />
      </div>
    </div>
  );
}
